// components/common/OnboardingTour/tourPhases.ts
//
// Definice kroků interaktivního průvodce dashboardem (onboarding po prvním upgradu).
// Průvodce je rozdělen do fází – každá fáze běží na jedné stránce (route).
// Poslední krok fáze s `advance: 'click'` vyžaduje, aby uživatel sám kliknul
// na zvýrazněný prvek (odkaz v menu, tlačítko…), čímž se přejde do další fáze.

export type StepSide = 'top' | 'bottom' | 'left' | 'right';

export interface TourStep {
  /** CSS selektor cíle. Bez selektoru se popover zobrazí uprostřed obrazovky. */
  target?: string;
  title: string;
  description: string;
  side?: StepSide;
  align?: 'start' | 'center' | 'end';
  /** 'button' (výchozí) = tlačítko Další; 'click' = uživatel musí kliknout na prvek. */
  advance?: 'button' | 'click';
  /** Krok se tiše přeskočí, pokud prvek na stránce není. */
  optional?: boolean;
}

export interface TourPhase {
  /** Route, na které fáze běží. */
  path: string;
  /** Selektor, na který se čeká před startem fáze (výchozí = první target). */
  waitFor?: string;
  /** Jak dlouho čekat na waitFor, než se fáze přeskočí (ms). */
  waitMs?: number;
  steps: TourStep[];
}

const OVERVIEW = '/dashboard';
const BONUSES = '/dashboard/bonuses';
const HISTORY = '/dashboard/bonus-history';
const REFERRALS = '/dashboard/referrals';
const SETTINGS = '/dashboard/settings';

export const TOUR_PHASES: TourPhase[] = [
  // ── Fáze 0: Přehled ────────────────────────────────────────────────
  {
    path: OVERVIEW,
    waitFor: '[data-tour="stat-bonus-revenue"]',
    steps: [
      {
        title: 'Vítej v DigiBrigi! 👋',
        description:
          'Rádi bychom ti ukázali, co všechno ve svém dashboardu najdeš. Průvodce tě krok za krokem provede všemi nástroji. Kdykoliv ho můžeš ukončit křížkem a později znovu spustit na stránce <strong>Průvodce</strong>.',
      },
      {
        target: '[data-tour="stat-bonus-revenue"]',
        title: '💰 Výdělky na bonusech',
        description: 'Celková částka, kterou sis vydělal plněním bonusů. S každým dokončeným bonusem tady uvidíš svůj reálný zisk.',
        side: 'bottom',
      },
      {
        target: '[data-tour="stat-referral-revenue"]',
        title: '👥 Výdělky na doporučeních',
        description: 'Kolik sis vydělal doporučováním DigiBrigi přátelům. Jak na to, ti ukážeme za chvíli v sekci Doporučení.',
        side: 'bottom',
      },
      {
        target: '[data-tour="stat-potential-revenue"]',
        title: '🌱 Odhadovaný výdělek',
        description: 'Součet očekávaných zisků z bonusů, které jsi dokončil. Porovnáním s reálnými výdělky vidíš, jak se ti daří naplňovat potenciál.',
        side: 'bottom',
      },
      {
        target: '[data-tour="stat-completed-bonuses"]',
        title: '✅ Dokončené bonusy',
        description: 'Počet bonusů, které jsi už úspěšně dokončil.',
        side: 'bottom',
      },
      {
        target: '[data-tour="earnings-chart"]',
        title: '📈 Graf výdělků',
        description: 'Zde vidíš vývoj svých výdělků v čase – bonusy a doporučení jsou barevně odlišeny.',
        side: 'top',
      },
      {
        target: '[data-tour="chart-controls"]',
        title: 'Přepínání grafu',
        description:
          'Pomocí těchto voleb si přepneš typ grafu (měsíční výdělky, kumulativní výdělky, počet bonusů, úspěšnost) a sledované období.',
        side: 'bottom',
        align: 'end',
      },
      {
        target: '[data-tour="nav-bonuses"]',
        title: 'Pojďme na bonusy 💰',
        description: 'Teď se podíváme na to nejdůležitější – dostupné bonusy. <strong>Klikni na „Bonusy“ v menu.</strong>',
        side: 'right',
        advance: 'click',
      },
    ],
  },

  // ── Fáze 1: Bonusy ────────────────────────────────────────────────
  {
    path: BONUSES,
    waitFor: '[data-tour="bonus-filters"]',
    waitMs: 20000,
    steps: [
      {
        target: '[data-tour="bonuses-sum"]',
        title: 'Tvůj možný zisk',
        description: 'Hned v nadpisu vidíš součet očekávaných zisků ze všech aktuálně zobrazených bonusů.',
        side: 'bottom',
        optional: true,
      },
      {
        target: '[data-tour="bonus-filters"]',
        title: '🔍 Filtry bonusů',
        description:
          'Bonusy si můžeš filtrovat podle <strong>typu</strong> (sázkové, bankovní…), <strong>označení</strong> (VIP, doporučené, výjimečné) a <strong>rizikovosti</strong>.',
        side: 'bottom',
      },
      {
        target: '[data-tour="bonus-sort"]',
        title: 'Řazení',
        description: 'Bonusy si seřadíš podle rizika, očekávaného zisku nebo podle toho, kdy končí jejich platnost.',
        side: 'bottom',
      },
      {
        target: '[data-tour="custom-bonus-btn"]',
        title: '➕ Vlastní bonus',
        description: 'S členstvím Premium si můžeš evidovat i vlastní bonusy, které jsi našel sami.',
        side: 'left',
        optional: true,
      },
      {
        target: '[data-tour="bonuses-grid"]',
        title: '🎁 Karty bonusů',
        description:
          'Každá karta ukazuje očekávaný zisk, míru rizika (budík vpravo nahoře), zbývající čas a rychlý přehled kroků.',
        side: 'top',
      },
      {
        target: '[data-tour="bonuses-grid"] [data-tour="bonus-open-btn"]',
        title: 'Otevři si detail bonusu',
        description: '<strong>Klikni na toto tlačítko</strong> a podíváme se, co detail bonusu obsahuje.',
        side: 'bottom',
        advance: 'click',
      },
    ],
  },

  // ── Fáze 2: Počáteční zůstatek (zobrazí se jen u nově zahájených bonusů) ──
  {
    path: BONUSES,
    waitFor: '[data-tour="start-balance-form"]',
    waitMs: 5000, // pokud se formulář neukáže, fáze se tiše přeskočí
    steps: [
      {
        target: '[data-tour="start-balance-form"]',
        title: '💰 Počáteční zůstatek',
        description:
          'Sem zadej svůj <strong>aktuální zůstatek u vybraného kasina</strong>. Až bonus dokončíš, z konečného zůstatku ti automaticky spočítáme tvůj skutečný zisk. Zůstatek ulož tlačítkem „Uložit zůstatek“.',
        side: 'bottom',
      },
    ],
  },

  // ── Fáze 3: Detail bonusu (modal) ─────────────────────────────────
  {
    path: BONUSES,
    waitFor: '[data-tour="bonus-modal-info"]',
    waitMs: 90000, // počká, než uživatel uloží počáteční zůstatek
    steps: [
      {
        target: '[data-tour="bonus-modal-info"]',
        title: 'ℹ️ Základní informace',
        description:
          'Název bonusu a společnost, u které ho plníš, očekávaný zisk, potřebný kapitál a míra rizika – vše na jednom místě.',
        side: 'bottom',
      },
      {
        target: '[data-tour="bonus-modal-steps"]',
        title: '🪜 Postup krok za krokem',
        description: 'Bonus plníš podle připravených kroků. Kliknutím na krok si zobrazíš jeho detailní návod.',
        side: 'bottom',
        optional: true,
      },
      {
        target: '[data-tour="bonus-modal-complete"]',
        title: '✓ Odškrtávání kroků',
        description: 'Každý splněný krok si označ jako dokončený. tvůj postup se ukládá, takže můžeš kdykoliv pokračovat.',
        side: 'top',
        optional: true,
      },
      {
        target: '[data-tour="bonus-modal-links"]',
        title: '📖 Podmínky a stránka bonusu',
        description: 'Odsud se dostaneš na úplné podmínky bonusu a na stránku společnosti, kde bonus plníš.',
        side: 'top',
      },
      {
        target: '[data-tour="modal-close"]',
        title: 'Zavři detail',
        description: '<strong>Klikni na křížek</strong> a půjdeme dál.',
        side: 'bottom',
        advance: 'click',
      },
    ],
  },

  // ── Fáze 4: Přechod na historii ───────────────────────────────────
  {
    path: BONUSES,
    waitFor: '[data-tour="nav-bonus-history"]',
    steps: [
      {
        target: '[data-tour="nav-bonus-history"]',
        title: '📋 Historie bonusů',
        description: 'Všechny dokončené bonusy najdeš v historii. <strong>Klikni na „Historie bonusů“.</strong>',
        side: 'right',
        advance: 'click',
      },
    ],
  },

  // ── Fáze 5: Historie bonusů ───────────────────────────────────────
  {
    path: HISTORY,
    waitFor: '[data-tour="history-table"]',
    waitMs: 20000,
    steps: [
      {
        target: '[data-tour="history-filters"]',
        title: 'Filtrování historie',
        description: 'Historii si vyfiltruješ podle typu bonusu.',
        side: 'bottom',
        optional: true,
      },
      {
        target: '[data-tour="history-controls"]',
        title: 'Období a stránkování',
        description: 'Zvol si sledované období (týden, měsíc, rok, vše) a počet záznamů na stránku.',
        side: 'bottom',
        optional: true,
      },
      {
        target: '[data-tour="history-table"]',
        title: 'Tvoje dokončené bonusy',
        description:
          'U každého bonusu vidíš datum, společnost a reálný zisk. Tužkou ✏️ můžeš zisk dodatečně upravit, nebo bonus vrátit mezi nedokončené.',
        side: 'top',
      },
      {
        target: '[data-tour="nav-referrals"]',
        title: '👥 Doporučení',
        description: 'Teď se podíváme, jak vydělávat doporučováním. <strong>Klikni na „Doporučení“.</strong>',
        side: 'right',
        advance: 'click',
      },
    ],
  },

  // ── Fáze 6: Doporučení (Referral) ─────────────────────────────────
  {
    path: REFERRALS,
    waitFor: '[data-tour="ref-link"]',
    waitMs: 20000,
    steps: [
      {
        target: '[data-tour="ref-stats"]',
        title: 'Přehled doporučení',
        description: 'Kolik lidí jsi doporučil, kolik máš aktuálně k výplatě a kolik sis na doporučeních vydělal celkem.',
        side: 'bottom',
      },
      {
        target: '[data-tour="ref-link"]',
        title: '🔗 tvůj referral odkaz',
        description:
          'Tohle je tvůj unikátní odkaz. Zkopíruj ho tlačítkem a pošli přátelům – za každého, kdo se přes něj zaregistruje a platí členství, dostáváš odměnu.',
        side: 'bottom',
      },
      {
        target: '[data-tour="ref-payout"]',
        title: '💳 Výplaty',
        description: 'Zde si nastavíš bankovní účet a necháš si vyplatit vydělané peníze.',
        side: 'top',
      },
      {
        target: '[data-tour="ref-auto-withdraw"]',
        title: '🔄 Automatický výběr',
        description: 'Můžeš si zapnout měsíční automatický výběr – peníze ti pak chodí samy, vždy do 7. dne měsíce.',
        side: 'top',
      },
      {
        target: '[data-tour="nav-settings"]',
        title: '⚙️ Nastavení',
        description: 'Na závěr ti ukážeme nastavení účtu. <strong>Klikni na „Nastavení“.</strong>',
        side: 'right',
        advance: 'click',
      },
    ],
  },

  // ── Fáze 7: Nastavení – záložky + notifikace ──────────────────────
  {
    path: SETTINGS,
    waitFor: '[data-tour="settings-tabs"]',
    waitMs: 20000,
    steps: [
      {
        target: '[data-tour="settings-tabs"]',
        title: 'Záložky nastavení',
        description: 'Nastavení je rozdělené do záložek – profil, notifikace, členství, zabezpečení, dokumenty a kontakty.',
        side: 'bottom',
      },
      {
        target: '[data-tour="settings-tab-notifications"]',
        title: '🔔 Notifikace',
        description: '<strong>Klikni na záložku „Notifikace“.</strong>',
        side: 'bottom',
        advance: 'click',
      },
    ],
  },

  // ── Fáze 8: Nastavení – přepínač notifikací ───────────────────────
  {
    path: SETTINGS,
    waitFor: '[data-tour="settings-notifications"]',
    waitMs: 8000,
    steps: [
      {
        target: '[data-tour="settings-notifications"]',
        title: 'Upozornění na nové bonusy',
        description:
          'Tímto přepínačem si zapneš push notifikace – dozvíš se o každém novém bonusu jako první a nic ti neuteče.',
        side: 'bottom',
      },
      {
        target: '[data-tour="settings-tab-membership"]',
        title: '💎 Členství',
        description: '<strong>Klikni na záložku „Členství“.</strong>',
        side: 'bottom',
        advance: 'click',
      },
    ],
  },

  // ── Fáze 9: Nastavení – členství + závěr ──────────────────────────
  {
    path: SETTINGS,
    waitFor: '[data-tour="settings-plan"]',
    waitMs: 8000,
    steps: [
      {
        target: '[data-tour="settings-plan"]',
        title: 'Tvůj aktuální plán',
        description: 'Zde vidíš své aktuální členství a jeho cenu.',
        side: 'bottom',
      },
      {
        target: '[data-tour="settings-billing"]',
        title: 'Správa předplatného',
        description:
          'Přes portál správy předplatného můžeš kdykoliv <strong>změnit platební metodu, upgradovat nebo zrušit</strong> své předplatné.',
        side: 'bottom',
        optional: true,
      },
      {
        target: '[data-tour="settings-features"]',
        title: 'Funkce tvého členství',
        description: 'Přehled všeho, co tvoje aktuální členství zahrnuje.',
        side: 'top',
        optional: true,
      },
      {
        title: 'A to je vše! 🎉',
        description:
          'Teď už víš, kde co najdeš. Přejeme hodně úspěšně dokončených bonusů! Průvodce si můžeš kdykoliv spustit znovu na stránce <strong>Průvodce</strong> v menu.',
      },
    ],
  },
];
